import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import AllJobSitesModal from "@WhiteCap/Components/OSNSubscriptions/JobSitesModal";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ShareListModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSubscriptionCustomerId = state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId;
    const data =
        state.WhiteCap.subscriptionsDashboardData.osnType === "accountLevel"
            ? state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions
            : state.WhiteCap.subscriptionsDashboardData.jobSiteSubscriptionsById[currentSubscriptionCustomerId];

    return {
        session: state.context.session,
        modalIsOpen: state.components.shareListModal.isOpen,
        subscriptionsData: data,
        notificationType: state.WhiteCap.subscriptionsDashboardData.osnType,
        osnTypeSelectionModalOpen: state.WhiteCap.subscriptionsDashboardData.osnTypeSelectionModalOpen,
        osnTypeSelectionConfirmed: state.WhiteCap.subscriptionsDashboardData.osnTypeSelectionConfirmed,
        billTosById: state.data.billTos.byId,
        shipTosById: state.data.shipTos.byId,
    };
};

const mapDispatchToProps = {};

export interface ShareListModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    radioGroup?: RadioGroupProps;
    radioButton?: RadioProps;
    bottomRowGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    proceedButton?: ButtonPresentationProps;
    gridItem?: GridItemProps;
    modalTitle?: TypographyProps;
    note?: TypographyProps;
    warning?: TypographyProps;
    noteGridItem?: GridItemProps;
}

export const shareListModalStyles: ShareListModalStyles = {
    modal: {
        size: 944,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                border-bottom: none;
                height: 50px;
            `,
            modalContent: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0px 30px 34px 30px;
                            `,

                            css`
                                padding: 0px 80px 50px 80px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            modalContainer: css`
                width: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                                height: auto;
                            `,
                            null,
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            scrim: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    container: { gap: 20 },
    radioGroup: {
        css: css`
            flex-direction: column;
            legend {
                display: none;
            }
            & > ${RadioStyle} {
                label {
                    margin-left: 0;
                    font-weight: 325;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 14px;
                                `,
                                null,
                                css`
                                    font-size: 1rem;
                                `,
                            ],
                            "min",
                        )}
                }
                margin-top: 0px !important;
                input {
                    display: contents;
                }
                input:checked + label::after {
                    content: "";
                    display: block;
                    height: 6px;
                    width: 6px;
                    border-radius: 50%;
                    background: black;
                    position: absolute;
                    left: 5px;
                    top: calc(50% - 3px);
                }
            }
            [class^="TypographyStyle"] {
                margin-bottom: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-left: 0;
                        `,
                        null,
                        css`
                            margin-left: 38px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    radioButton: {
        css: css`
            margin-bottom: 18px;
            input {
                margin-left: 0;
                width: 100%;
            }
            &:last-of-type {
                margin-bottom: 0;
            }
        `,
    },
    bottomRowGridItem: {
        width: 12,
        css: css`
            gap: 1rem;
            justify-content: center;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            width: auto;
        `,
    },
    proceedButton: {
        variant: "primary",
        css: css`
            width: auto;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            padding-top: 0;
        `,
    },
    noteGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    modalTitle: {
        variant: "h3",
        weight: 325,
        lineHeight: "40px",
        css: css`
            margin-bottom: 12px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            line-height: 30px;
                            font-size: 16px;
                        `,
                        null,
                        css`
                            line-height: 40px;
                            font-size: 20px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    note: {
        variant: "p",
        weight: 325,
        lineHeight: "25px",
        css: css`
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                            margin-top: 16px;
                        `,
                        null,
                        css`
                            font-size: 16px;
                            margin-top: 36px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    warning: {
        variant: "p",
        weight: 325,
        css: css`
            .billto_highlight {
                font-weight: 450;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                            line-height: 20px;
                        `,
                        null,
                        css`
                            font-size: 20px;
                            line-height: 40px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = shareListModalStyles;

const ChangeOsnTypeModal = ({
    session,
    subscriptionsData,
    notificationType,
    osnTypeSelectionModalOpen,
    osnTypeSelectionConfirmed,
    billTosById,
    shipTosById,
}: Props) => {
    if (!session.billToId || !session.shipToId) {
        return null;
    }
    const dispatch = useDispatch();
    const billToId = session.billToId;
    const shipToId = session.shipToId;
    const billToDetails = billTosById[billToId];
    const shipToDetails = shipTosById[shipToId];
    const [notificationTypeSelection, setNotificationTypeSelection] = useState(notificationType);
    const [inProgress, setInProgress] = useState(false);
    const [isOpenIndividualJobSiteSelectionModal, setIsOpenIndividualJobSiteSelectionModal] = useState(false);

    const modalCloseHandler = () => {
        dispatch({
            type: "Custom/SubscriptionDashboard/ModifyOsnTypeSelectionModalOpenState",
            value: false,
        });
    };

    const notificationTypeSelectionChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotificationTypeSelection(event.currentTarget.value as "accountLevel" | "individualJobSiteLevel");
    };

    const handleProceed = () => {
        const warningPopup = osnTypeSelectionConfirmed && notificationType === "individualJobSiteLevel";

        if (warningPopup) {
            setNotificationTypeSelection("accountLevel");
            dispatch({
                type: "Custom/SubscriptionDashboard/ChangeNotificationType",
                value: "accountLevel",
            });
            return;
        }

        if (notificationTypeSelection === "individualJobSiteLevel") {
            setIsOpenIndividualJobSiteSelectionModal(true);
        } else {
            dispatch({
                type: "Custom/SubscriptionDashboard/ChangeNotificationType",
                value: "accountLevel",
            });
        }
    };

    return (
        <>
            {isOpenIndividualJobSiteSelectionModal ? (
                <AllJobSitesModal
                    isModalOpen={isOpenIndividualJobSiteSelectionModal}
                    closeModalHandler={() => setIsOpenIndividualJobSiteSelectionModal(false)}
                    viewOnly={false}
                />
            ) : (
                <Modal {...styles.modal} isOpen={osnTypeSelectionModalOpen} handleClose={modalCloseHandler}>
                    <GridContainer {...styles.container}>
                        {osnTypeSelectionConfirmed && notificationType === "individualJobSiteLevel" ? (
                            <StyledWrapper>
                                <GridItem {...styles.gridItem}>
                                    <Typography {...styles.warning}>
                                        You are about to manage subscriptions for{" "}
                                        <span className="billto_highlight">ALL</span> Jobsites for the account{" "}
                                        <span className="billto_highlight">
                                            #${billToDetails?.customerNumber} |{" "}
                                            {billToDetails?.properties?.customerName}.
                                        </span>
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.gridItem}>
                                    <Typography {...styles.warning}>
                                        {siteMessage("OSN_Individual_Job_Site_Switch_Message")}
                                    </Typography>
                                </GridItem>
                            </StyledWrapper>
                        ) : (
                            <>
                                <GridItem {...styles.gridItem}>
                                    <Typography {...styles.modalTitle}>
                                        How would you like to manage your Order Status Notifications?{" "}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.gridItem}>
                                    <RadioGroup
                                        {...styles.radioGroup}
                                        value={notificationTypeSelection}
                                        onChangeHandler={notificationTypeSelectionChangeHandler}
                                        data-test-selector="shareListOption"
                                    >
                                        <Radio {...styles.radioButton} value="accountLevel">
                                            {`Account level (includes all Jobsites ${subscriptionsData?.totalSites})`}
                                        </Radio>
                                        <Radio {...styles.radioButton} value="individualJobSiteLevel">
                                            {translate("Individual Jobsite")}
                                        </Radio>
                                    </RadioGroup>
                                </GridItem>
                                <GridItem {...styles.noteGridItem}>
                                    <Typography {...styles.note}>
                                        You will be able to change your selection later.
                                    </Typography>
                                </GridItem>
                            </>
                        )}
                        <GridItem {...styles.bottomRowGridItem}>
                            <Button
                                {...styles.cancelButton}
                                onClick={modalCloseHandler}
                                data-test-selector="cancelButton"
                            >
                                {translate("Cancel")}
                            </Button>

                            <Button {...styles.proceedButton} onClick={handleProceed} disabled={inProgress}>
                                {translate("Proceed")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeOsnTypeModal);
